import { type SupportedLocale, type SupportedLocaleMap } from '@zep-us/i18n';
import { type BuildProFeatureCoachmarkFeature } from '../BuildProFeatureCoachmark/types';

/** 업셀 가이드 이미지를 준비해 둔 로케일. 나머지는 `resolveFeatureUpsellImageSrc` 가 영어로 접는다. */
export type GuideLocale = 'ko' | 'en' | 'ja' | 'id' | 'th';

// 코치마크와 업셀 모달은 동일한 PRO 피처 집합을 다루므로 키 유니온을 단일 소스로 공유한다.
export type FeatureUpsellFeature = BuildProFeatureCoachmarkFeature;
type FeatureUpsellTranslationKey = 'build:sectionAiTutor.add' | 'build:aiTutorIntro.title' | 'build:aiTutorIntro.description' | 'build:aiTutorIntro.notice1' | 'build:aiTutorIntro.notice2' | 'build:aiTutorIntro.limitation' | 'build:build.option.aiTranslationTitle' | 'build:featureUpsell.aiTranslation.title' | 'build:featureUpsell.aiTranslation.description' | 'build:featureUpsell.aiTranslation.notice1' | 'build:featureUpsell.aiTranslation.notice2' | 'build:settings.nextQuizset.title' | 'build:featureUpsell.nextQuizset.title' | 'build:featureUpsell.nextQuizset.description' | 'build:featureUpsell.nextQuizset.notice1' | 'build:featureUpsell.nextQuizset.notice2' | 'build:hideFromExplore.title' | 'build:featureUpsell.hideFromLibrary.title' | 'build:featureUpsell.hideFromLibrary.description' | 'build:featureUpsell.hideFromLibrary.notice1' | 'build:featureUpsell.hideFromLibrary.notice2';
interface FeatureUpsellConfig {
  images: SupportedLocaleMap<GuideLocale>;
  modalTitleKey: FeatureUpsellTranslationKey;
  titleKey: FeatureUpsellTranslationKey;
  descriptionKey: FeatureUpsellTranslationKey;
  noticeKeys: FeatureUpsellTranslationKey[];
  footnoteKey?: FeatureUpsellTranslationKey;
  from: string;
}
export const resolveFeatureUpsellImageSrc = (images: SupportedLocaleMap<GuideLocale>, locale: SupportedLocale) => images[locale] ?? images.en;
export const FEATURE_UPSELL_CONFIG: Record<FeatureUpsellFeature, FeatureUpsellConfig> = {
  AI_TUTOR: {
    images: {
      ko: '/assets/ai-tutor/modal/ai-tutor-ko.webp',
      en: '/assets/ai-tutor/modal/ai-tutor-en.webp',
      ja: '/assets/ai-tutor/modal/ai-tutor-ja.webp',
      id: '/assets/ai-tutor/modal/ai-tutor-id.webp',
      th: '/assets/ai-tutor/modal/ai-tutor-th.webp'
    },
    modalTitleKey: 'build:sectionAiTutor.add',
    titleKey: 'build:aiTutorIntro.title',
    descriptionKey: 'build:aiTutorIntro.description',
    noticeKeys: ['build:aiTutorIntro.notice1', 'build:aiTutorIntro.notice2'],
    footnoteKey: 'build:aiTutorIntro.limitation',
    from: 'aiTutor'
  },
  AI_QUIZSET_TRANSLATION: {
    images: {
      ko: '/assets/ai-tutor/modal/ai-translation-ko.webp',
      en: '/assets/ai-tutor/modal/ai-translation-en.webp',
      ja: '/assets/ai-tutor/modal/ai-translation-ja.webp',
      id: '/assets/ai-tutor/modal/ai-translation-id.webp',
      th: '/assets/ai-tutor/modal/ai-translation-th.webp'
    },
    modalTitleKey: 'build:build.option.aiTranslationTitle',
    titleKey: 'build:featureUpsell.aiTranslation.title',
    descriptionKey: 'build:featureUpsell.aiTranslation.description',
    noticeKeys: ['build:featureUpsell.aiTranslation.notice1', 'build:featureUpsell.aiTranslation.notice2'],
    from: 'aiTranslation'
  },
  CONTINUE_NEXT_QUIZSET: {
    images: {
      ko: '/assets/ai-tutor/modal/next-quizset-ko.webp',
      en: '/assets/ai-tutor/modal/next-quizset-en.webp',
      ja: '/assets/ai-tutor/modal/next-quizset-ja.webp',
      id: '/assets/ai-tutor/modal/next-quizset-id.webp',
      th: '/assets/ai-tutor/modal/next-quizset-th.webp'
    },
    modalTitleKey: 'build:settings.nextQuizset.title',
    titleKey: 'build:featureUpsell.nextQuizset.title',
    descriptionKey: 'build:featureUpsell.nextQuizset.description',
    noticeKeys: ['build:featureUpsell.nextQuizset.notice1', 'build:featureUpsell.nextQuizset.notice2'],
    from: 'nextQuizset'
  },
  PRIVATE_QUIZ: {
    images: {
      ko: '/assets/ai-tutor/modal/hide-from-library-ko.webp',
      en: '/assets/ai-tutor/modal/hide-from-library-en.webp',
      ja: '/assets/ai-tutor/modal/hide-from-library-ja.webp',
      id: '/assets/ai-tutor/modal/hide-from-library-id.webp',
      th: '/assets/ai-tutor/modal/hide-from-library-th.webp'
    },
    modalTitleKey: 'build:hideFromExplore.title',
    titleKey: 'build:featureUpsell.hideFromLibrary.title',
    descriptionKey: 'build:featureUpsell.hideFromLibrary.description',
    noticeKeys: ['build:featureUpsell.hideFromLibrary.notice1', 'build:featureUpsell.hideFromLibrary.notice2'],
    from: 'isPublic'
  }
};